import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Loader2, Sparkles, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import ShareMenu from '@/components/ShareMenu';

const readAsDataURL = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const Avatar = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [source, setSource] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) nav('/login');
  }, [user, loading, nav]);

  const pick = async (file) => {
    if (!file) return;
    const b64 = await readAsDataURL(file);
    setSource(b64);
    setResult(null);
  };

  const generate = async () => {
    if (!source) return;
    setBusy(true);
    try {
      const { data } = await api.post('/avatar/generate', { image_b64: source });
      setResult(`data:${data.mime_type};base64,${data.image_b64}`);
      toast.success('Your healthiest self, revealed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create avatar. Try again.');
    } finally { setBusy(false); }
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result; a.download = 'wellth-healthiest-self.png'; a.click();
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 sm:py-16" data-testid="avatar-container">
      <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">Vision</span>
      <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#1A2421] font-medium leading-tight">Meet your healthiest self.</h1>
      <p className="mt-3 text-[#4A5A53] font-light max-w-xl">
        Upload a full-body photo. Our AI reimagines you 6&ndash;12 months into your journey, using your intake as guidance.
      </p>

      <div className="mt-10 grid md:grid-cols-2 gap-8">
        {/* Source */}
        <div className="bg-white border border-[#E5E1D8] rounded-2xl p-6">
          <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">Your photo</label>
          <div className="mt-3 aspect-[3/4] rounded-xl border-2 border-dashed border-[#E5E1D8] overflow-hidden bg-[#F2EFE9] flex items-center justify-center">
            {source ? (
              <img src={source} alt="You" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center text-[#4A5A53] p-6">
                <Camera className="w-8 h-8 mx-auto mb-2 opacity-60" strokeWidth={1.5} />
                <p className="text-sm font-light">Full-body shot works best.<br/>Good lighting, front-facing.</p>
              </div>
            )}
          </div>
          <label className="w-full mt-4 block">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => pick(e.target.files?.[0])}
              data-testid="avatar-file-input"
            />
            <div className="w-full rounded-full bg-white border border-[#E5E1D8] hover:border-[#7E9979] text-[#1A2421] flex items-center justify-center gap-2 h-12 cursor-pointer">
              <Camera className="w-4 h-4" strokeWidth={1.5} /> {source ? 'Choose different photo' : 'Upload full-body photo'}
            </div>
          </label>
          <Button
            onClick={generate}
            disabled={!source || busy}
            className="mt-3 w-full rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white h-12 gap-2"
            data-testid="avatar-generate-button"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Envisioning… (30–60s)</> : <><Sparkles className="w-4 h-4" strokeWidth={1.5} /> Generate my healthiest self</>}
          </Button>
        </div>

        {/* Result */}
        <div className="bg-white border border-[#E5E1D8] rounded-2xl p-6" data-testid="avatar-result-card">
          <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">Your healthiest self</label>
          <motion.div
            key={result ? 'ready' : 'empty'}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 aspect-[3/4] rounded-xl overflow-hidden bg-[#F2EFE9] flex items-center justify-center relative"
          >
            {result ? (
              <img src={result} alt="Healthiest self" className="w-full h-full object-cover" />
            ) : busy ? (
              <div className="text-center text-[#4A5A53]"><Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[#4A7C59]" /><p className="text-sm font-light">Painting your vision…</p></div>
            ) : (
              <div className="text-center text-[#4A5A53] p-6 font-light text-sm">Your reimagined portrait will appear here.</div>
            )}
          </motion.div>
          {result && (
            <div className="mt-4 flex gap-2">
              <Button onClick={download} variant="outline" className="rounded-full border-[#E5E1D8] gap-2" data-testid="avatar-download-button">
                <Download className="w-4 h-4" strokeWidth={1.5} /> Download
              </Button>
              <ShareMenu
                label="Share"
                text="I just saw my healthiest self on Wellth. Health is the greatest wealth. 🌱"
                testId="avatar-share-button"
              />
            </div>
          )}
        </div>
      </div>

      <p className="mt-8 text-xs text-[#4A5A53] font-light italic max-w-xl">
        Wellth&rsquo;s Avatar is an aspirational visualization, not a medical prediction. Real transformations happen through daily habits &mdash; this is your compass.
      </p>
    </div>
  );
};

export default Avatar;
